import { useEffect, useState } from "react";
import { Badge, Banner, BlockStack, Button, Card, Collapsible, InlineStack, Link, List, Spinner, Text } from "@shopify/polaris";
import supabase from "../../supabase-client";
import { formatFriendlyDate } from "../../utility/funcs";

// The AI "expert" opinion on a service awaiting approval (services.ai_review,
// written by the service_expert_review edge function from the car's whole
// service history). It starts on its own when the service's lines are saved;
// the button re-runs it.

const VERDICT = {
  ok: { tone: "success", label: "Sin observaciones" },
  revisar: { tone: "warning", label: "Revisar" },
  alerta: { tone: "critical", label: "Alerta" },
};
const SEVERITY = { alta: "critical", media: "warning", baja: undefined };
const POLL_MS = 5000;
const POLL_MAX = 36; // ~3 minutes

function ExpertReviewCard({ serviceId, initial, reviewedAt }) {
  const [review, setReview] = useState(initial);
  const [at, setAt] = useState(reviewedAt);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [showAsk, setShowAsk] = useState(false);

  const running = review?.status === "running";

  // The automatic run finishes after the page loaded: poll until it lands.
  useEffect(() => {
    if (!running) return;
    let n = 0;
    const t = setInterval(async () => {
      n += 1;
      const { data } = await supabase
        .from("services")
        .select("ai_review, ai_reviewed_at")
        .eq("id", serviceId)
        .maybeSingle();
      if (data?.ai_review?.status !== "running" || n >= POLL_MAX) {
        setReview(data?.ai_review ?? null);
        setAt(data?.ai_reviewed_at ?? null);
        clearInterval(t);
      }
    }, POLL_MS);
    return () => clearInterval(t);
  }, [running, serviceId]);

  const rerun = async () => {
    setBusy(true);
    setError(null);
    setReview({ status: "running" });
    try {
      const { data, error: err } = await supabase.functions.invoke("service_expert_review", {
        body: { service_id: Number(serviceId), wait: true },
      });
      if (err) {
        let detail = "";
        try {
          detail = (await err.context?.json())?.error || "";
        } catch {
          /* non-JSON body */
        }
        throw new Error(detail || err.message);
      }
      setReview(data);
      setAt(new Date().toISOString());
    } catch (e) {
      setError(e.message);
      setReview(initial);
    } finally {
      setBusy(false);
    }
  };

  const v = VERDICT[review?.verdict];

  return (
    <Card>
      <BlockStack gap="300">
        <InlineStack align="space-between" blockAlign="center">
          <InlineStack gap="200" blockAlign="center">
            <Text variant="headingSm" as="h3">
              Opinión experta (IA)
            </Text>
            {review?.status === "done" && v && <Badge tone={v.tone}>{v.label}</Badge>}
          </InlineStack>
          <Button variant="plain" loading={busy} disabled={running && !busy} onClick={rerun}>
            {review?.status === "done" ? "Volver a revisar" : "Revisar"}
          </Button>
        </InlineStack>

        {running && (
          <InlineStack gap="200" blockAlign="center">
            <Spinner size="small" />
            <Text tone="subdued">Revisando el historial de la unidad…</Text>
          </InlineStack>
        )}
        {!review && !running && (
          <Text tone="subdued">Aún no se ha revisado este servicio.</Text>
        )}
        {review?.status === "error" && (
          <Banner tone="critical">No se pudo revisar: {review.error}</Banner>
        )}
        {error && <Banner tone="critical">{error}</Banner>}

        {review?.status === "done" && (
          <BlockStack gap="300">
            <Text fontWeight="semibold">{review.headline}</Text>

            {review.findings?.length > 0 && (
              <BlockStack gap="200">
                {review.findings.map((f, i) => (
                  <InlineStack key={i} gap="200" blockAlign="start" wrap={false}>
                    <Badge tone={SEVERITY[f.severity]}>{f.severity}</Badge>
                    <Text variant="bodySm">
                      <Text as="span" fontWeight="semibold">{f.title}.</Text> {f.detail}{" "}
                      {(f.services || []).map((id) => (
                        <Link key={id} url={`/services/${id}`} target="_blank">
                          {`#${id} `}
                        </Link>
                      ))}
                    </Text>
                  </InlineStack>
                ))}
              </BlockStack>
            )}

            {(review.questions_for_provider?.length > 0 || review.evidence_to_request?.length > 0) && (
              <InlineStack>
                <Button variant="plain" disclosure={showAsk ? "up" : "down"} onClick={() => setShowAsk((x) => !x)}>
                  Ver qué pedir al proveedor
                </Button>
              </InlineStack>
            )}
            <Collapsible open={showAsk} id="expert-review-ask" transition={false}>
            <BlockStack gap="200">
            {review.questions_for_provider?.length > 0 && (
              <BlockStack gap="100">
                <Text fontWeight="semibold">Preguntar al proveedor</Text>
                <List>
                  {review.questions_for_provider.map((q, i) => (
                    <List.Item key={i}>{q}</List.Item>
                  ))}
                </List>
              </BlockStack>
            )}
            {review.evidence_to_request?.length > 0 && (
              <BlockStack gap="100">
                <Text fontWeight="semibold">Pedir evidencia</Text>
                <List>
                  {review.evidence_to_request.map((q, i) => (
                    <List.Item key={i}>{q}</List.Item>
                  ))}
                </List>
              </BlockStack>
            )}
            </BlockStack>
            </Collapsible>

            <Text tone="subdued" variant="bodySm">
              {`Basado en ${review.history_count ?? 0} servicios previos`}
              {at ? ` · ${formatFriendlyDate(at)}` : ""}
            </Text>
          </BlockStack>
        )}
      </BlockStack>
    </Card>
  );
}

export default ExpertReviewCard;
